import { CommandBlock } from "@/components/CommandBlock";
import { SetupStep } from "@/components/SetupStep";
import { useCopyToClipboard } from "@/lib/useCopyToClipboard";
import { eksRegion } from "./shared";

function awsPartition(region: string) {
  if (region.startsWith("cn-")) return "aws-cn";
  if (region.startsWith("us-gov-")) return "aws-us-gov";

  return "aws";
}

function accessEntryCommands(
  region: string,
  cluster: string,
  principal: string,
) {
  return [
    `aws eks create-access-entry --region ${region} \\`,
    `  --cluster-name ${cluster} --principal-arn ${principal}`,
    "",
    `aws eks associate-access-policy --region ${region} \\`,
    `  --cluster-name ${cluster} --principal-arn ${principal} \\`,
    `  --policy-arn arn:${awsPartition(region)}:eks::aws:cluster-access-policy/AmazonEKSViewPolicy \\`,
    "  --access-scope type=cluster",
  ].join("\n");
}

export function EksAccessSteps({
  credentialType,
  address,
  clusterName,
}: {
  credentialType: string;
  address: string;
  clusterName: string;
}) {
  const { copiedField, copyToClipboard } = useCopyToClipboard();

  if (credentialType !== "AwsIamRole" && credentialType !== "AwsAccessKey") {
    return null;
  }

  const ownIdentity = credentialType === "AwsIamRole";
  const commands = accessEntryCommands(
    eksRegion(address) ?? "<region>",
    clusterName.trim() || "<cluster-name>",
    ownIdentity ? "<role-arn>" : "<user-arn>",
  );

  return (
    <ol className="mt-2">
      {ownIdentity && (
        <SetupStep
          label="1"
          state="current"
          title="Give each gateway or connector an IAM role"
        >
          <p className="mt-1 text-sm text-foreground/70">
            Attach an IAM role where each gateway or connector runs. Use an
            instance profile on EC2, a task role on ECS, or EKS Pod Identity on
            Kubernetes. The role doesn't need any IAM permissions of its own.
          </p>
        </SetupStep>
      )}
      <SetupStep
        label={ownIdentity ? "2" : "1"}
        state="current"
        title={
          ownIdentity
            ? "Give the role access to the cluster"
            : "Give the IAM user access to the cluster"
        }
        last
      >
        <p className="mt-1 mb-2 text-sm text-foreground/70">
          {ownIdentity
            ? "Create an access entry for the role"
            : "Create an access entry for the user the key belongs to"}{" "}
          and ensure the cluster's authentication mode includes the EKS API.
          Replace{" "}
          <code className="text-foreground">
            {ownIdentity ? "<role-arn>" : "<user-arn>"}
          </code>{" "}
          with the {ownIdentity ? "role's" : "IAM user's"} ARN.
        </p>
        <CommandBlock
          command={commands}
          field="eksAccessEntry"
          copiedField={copiedField}
          onCopy={copyToClipboard}
        />
      </SetupStep>
    </ol>
  );
}
